import { createContext, useContext, useEffect, useState } from "react";

// Spanish (es) overrides — covers the most user-facing strings.
// Any key not here falls back to EN, then PT (handled in useT).
const ES_OVERRIDES = {
  // nav / common
  "nav.product": "Producto",
  "nav.pricing": "Precios",
  "nav.demo": "Demo en vivo",
  "nav.integrations": "Integraciones",
  "nav.about": "Quiénes somos",
  "nav.partners": "Socios",
  "nav.signin": "Entrar",
  "nav.signup": "Comenzar gratis →",
  "nav.dashboard": "Abrir panel →",
  "common.cancel": "Cancelar",
  "common.save": "Guardar",
  "common.delete": "Eliminar",
  "common.loading": "Cargando...",
  "common.email": "Email",
  "common.password": "Contraseña",
  "common.name": "Nombre",
  "common.connect": "Conectar",
  "common.skip": "Saltar",
  "common.back": "Volver",
  "common.copy": "Copiar",
  "common.revoke": "Revocar",
  // landing hero
  "landing.overline": "◆ DIAGNÓSTICO DE SOPORTE CON IA · HECHO PARA INGENIEROS",
  "landing.hero.from": "De",
  "landing.hero.minutes30": "30 minutos",
  "landing.hero.to": "a",
  "landing.hero.minutes2": "2 minutos",
  "landing.hero.persupport": "por ticket de soporte.",
  "landing.hero.subtitle": "Ratchet se conecta al tablero, los logs, las bases de datos y el código que tu equipo ya usa — 48 integraciones listas. Parsea, consulta, correlaciona y escribe la resolución — automáticamente. Tu equipo se enfoca en entregar, no en copiar-pegar log queries.",
  "landing.cta.signup": "Iniciar prueba gratis",
  "landing.cta.demo": "Ver demo en vivo",
  "landing.stat.lessTime": "menos tiempo por ticket",
  "landing.stat.sources": "fuentes de datos unificadas",
  "landing.stat.audit": "informes auditables",
  "landing.connects": "Se conecta a",
  "landing.allIntegrations": "Ver las 48 integraciones",
  "blog.title": "Notas de ingeniería",
  "integrations.page.overline": "// integraciones · {n} conectores",
  "integrations.page.titleTag": "{n} integraciones",
  "integrations.page.title": "Conecta Ratchet al stack que tu equipo ya usa.",
  "integrations.page.subtitle": "Tableros, logs, bases de datos, repositorios, proveedores de IA y chat. Ratchet cruza todas estas fuentes para llegar a la causa raíz de cada ticket.",
  "integrations.page.filter": "Filtrar por categoría",
  "integrations.page.all": "Todas",
  "integrations.page.ready": "disponible",
  "integrations.page.soon": "próximamente",
  "integrations.page.missingTitle": "¿No encontraste tu herramienta?",
  "integrations.page.missingDesc": "Cuéntanos qué usa tu equipo. Priorizamos nuevos conectores según la demanda de los clientes.",
  "integrations.page.request": "Pedir integración",
  "landing.proof.overline": "Prueba",
  "landing.proof.timeSaved": "Tiempo medio ahorrado por ticket",
  // sidebar
  "side.dashboard": "Panel",
  "side.cards": "Tickets",
  "side.knowledge": "Conocimiento",
  "side.integrations": "Integraciones",
  "side.team": "Equipo",
  "side.apikeys": "API Keys",
  "side.billing": "Facturación",
  "side.workspace": "Workspace",
  "side.signout": "Salir",
  // pricing
  "pricing.contactSales": "Hablar con ventas",
  // billing
  "billing.overline": "// facturación · suscripción · uso",
  "billing.title": "Plan y facturación",
  "billing.currentlyOn": "Actualmente en",
  "billing.active": "ACTIVO",
  "billing.payCard": "Pagar con tarjeta",
  "billing.payPaypal": "PayPal",
  "billing.startingTier": "Plan inicial",
  "billing.notes": "Notas",
  "billing.confirming": "Confirmando pago...",
  "billing.confirmingDesc": "Esto puede tardar unos segundos.",
  "billing.confirmed": "Pago confirmado",
  "billing.confirmedDesc": "Tu plan ahora es",
  "billing.goDash": "Ir al panel",
  // cards
  "cards.run": "Crear ticket",
  "cards.col.saved": "Ahorrado",
  "cards.col.date": "Fecha",
  // card detail
  "card.back": "Volver a tickets",
  "card.pipeline": "Pipeline",
  "card.aiReport": "Informe IA",
  "card.awaiting": "Esperando análisis...",
  "demo.working": "Trabajando",
  "demo.report.hypotheses": "Hipótesis",
  "demo.report.facts": "Hechos",
  "demo.report.rootCause": "Causa raíz",
  "demo.report.resolution": "Resolución",
  "demo.report.saved": "ahorrado",
  // integ
  "integ.overline": "// integraciones · catálogo · 30+",
  "integ.title": "Integraciones",
  "integ.subtitle": "Conecta tus fuentes de datos. Las credenciales se cifran con Fernet.",
  "integ.add": "Agregar integración",
  "integ.type": "Tipo",
  "integ.test": "Probar",
  "integ.empty": "Sin integraciones conectadas todavía.",
  "integ.confirmDelete": "¿Eliminar esta integración?",
  "integ.category.ai": "IA",
  "integ.category.boards": "Boards",
  "integ.category.logs": "Logs",
  "integ.category.repos": "Repositorios",
  "integ.category.databases": "Bases de datos",
  "integ.category.chat": "Chat",
  // admin
  "admin.overline": "// super-admin",
  "admin.title": "Super Admin",
  "admin.kpi.orgs": "Organizaciones",
  "admin.kpi.users": "Usuarios",
  "admin.kpi.cards": "Tickets",
  "admin.dist": "Distribución por plan",
  "admin.orgs": "Organizaciones",
};

const DICT = {
  // ============ NAV / COMMON ============
  "nav.product": { pt: "Produto", en: "Product" },
  "nav.pricing": { pt: "Preços", en: "Pricing" },
  "nav.demo": { pt: "Demo ao vivo", en: "Live Demo" },
  "nav.integrations": { pt: "Integrações", en: "Integrations" },
  "nav.about": { pt: "Quem somos", en: "About", es: "Quiénes somos" },
  "nav.partners": { pt: "Parceiros", en: "Partners", es: "Socios" },
  "about.role.dev": { pt: "Desenvolvimento", en: "Engineering", es: "Desarrollo" },
  "about.role.sales": { pt: "Vendas & Parcerias", en: "Sales & Partnerships", es: "Ventas y Alianzas" },
  "nav.signin": { pt: "Entrar", en: "Sign in" },
  "nav.signup": { pt: "Começar grátis →", en: "Start free →" },
  "nav.dashboard": { pt: "Abrir painel →", en: "Open dashboard →" },
  "common.cancel": { pt: "Cancelar", en: "Cancel" },
  "common.save": { pt: "Salvar", en: "Save" },
  "common.delete": { pt: "Remover", en: "Remove" },
  "common.loading": { pt: "Carregando...", en: "Loading..." },
  "common.email": { pt: "Email", en: "Email" },
  "common.password": { pt: "Senha", en: "Password" },
  "common.name": { pt: "Nome", en: "Name" },
  "common.connect": { pt: "Conectar", en: "Connect" },
  "common.skip": { pt: "Pular", en: "Skip" },
  "common.back": { pt: "Voltar", en: "Back" },
  "common.copy": { pt: "Copiar", en: "Copy" },
  "common.revoke": { pt: "Revogar", en: "Revoke" },

  // ============ LANDING ============
  "landing.overline": { pt: "◆ DIAGNÓSTICO DE SUPORTE COM IA · FEITO PRA ENGENHEIROS",
                       en: "◆ AI-NATIVE SUPPORT DIAGNOSTICS · BUILT FOR ENGINEERS" },
  "landing.hero.from": { pt: "De", en: "From" },
  "landing.hero.minutes30": { pt: "30 minutos", en: "30 minutes" },
  "landing.hero.to": { pt: "para", en: "to" },
  "landing.hero.minutes2": { pt: "2 minutos", en: "2 minutes" },
  "landing.hero.persupport": { pt: "por card de suporte.", en: "per support card." },
  "landing.hero.subtitle": {
    pt: "O Ratchet conecta no board, nos logs, nos bancos e no código que seu time já usa — 48 integrações prontas. Faz parse, query, correlaciona e escreve a resolução — automaticamente. Sua equipe foca em entregar, não em copiar-colar log queries.",
    en: "Ratchet plugs into the board, logs, databases and code your team already uses — 48 ready-made connectors. It parses, queries, correlates and writes the resolution — automatically. Your engineers focus on shipping, not on copy-pasting log queries.",
  },
  "landing.cta.signup": { pt: "Iniciar trial gratuito", en: "Start free trial" },
  "landing.cta.demo": { pt: "Ver demo ao vivo", en: "Watch live demo" },
  "landing.stat.lessTime": { pt: "menos tempo por card", en: "less time per card" },
  "landing.stat.sources": { pt: "fontes de dados unificadas", en: "data sources unified" },
  "landing.stat.audit": { pt: "relatórios auditáveis", en: "audit-ready reports" },
  "landing.connects": { pt: "Conecta-se a", en: "Connects to" },
  "landing.allIntegrations": { pt: "Ver as 48 integrações", en: "See all 48 integrations" },
  "blog.title": { pt: "Notas de engenharia", en: "Engineering notes" },
  "integrations.page.overline": { pt: "// integrações · {n} conectores", en: "// integrations · {n} connectors" },
  "integrations.page.titleTag": { pt: "{n} integrações", en: "{n} integrations" },
  "integrations.page.title": { pt: "Conecte o Ratchet à stack que seu time já usa.", en: "Connect Ratchet to the stack your team already uses." },
  "integrations.page.subtitle": {
    pt: "Boards, logs, bancos de dados, repositórios, provedores de IA e chat. O Ratchet cruza todas essas fontes para chegar à causa raiz de cada card.",
    en: "Boards, logs, databases, repositories, AI providers and chat. Ratchet correlates all of these sources to reach the root cause of every card.",
  },
  "integrations.page.filter": { pt: "Filtrar por categoria", en: "Filter by category" },
  "integrations.page.all": { pt: "Todas", en: "All" },
  "integrations.page.ready": { pt: "disponível", en: "available" },
  "integrations.page.soon": { pt: "em breve", en: "coming soon" },
  "integrations.page.missingTitle": { pt: "Não achou sua ferramenta?", en: "Don't see your tool?" },
  "integrations.page.missingDesc": {
    pt: "Conta pra gente o que seu time usa. Priorizamos novos conectores pela demanda dos clientes.",
    en: "Tell us what your team uses. We prioritize new connectors by customer demand.",
  },
  "integrations.page.request": { pt: "Pedir integração", en: "Request an integration" },
  "landing.flow.overline": { pt: "// fluxo · 9 etapas · zero troca de contexto",
                            en: "// flow · 9 steps · zero context-switch" },
  "landing.flow.title": { pt: "O pipeline mecânico que um engenheiro sênior rodaria.",
                         en: "The mechanical pipeline a senior engineer would run." },
  "landing.flow.titleHL": { pt: " Sem o engenheiro.", en: " Without the engineer." },
  "landing.proof.overline": { pt: "Prova", en: "Proof point" },
  "landing.proof.timeSaved": { pt: "Tempo médio economizado por card", en: "Average time saved per card" },
  "landing.proof.timeSavedNote": { pt: "Baseado em pilotos internos iniciais",
                                  en: "Based on early adopters' internal pilots" },
  "landing.proof.cards": { pt: "Cards processados em pilotos iniciais", en: "Cards processed in early pilots" },
  "landing.proof.cardsNote": { pt: "Squads de payments, infra e plataforma",
                              en: "Across payments, infra and platform squads" },
  "landing.proof.cost": { pt: "Custo de engenharia recuperado por ano", en: "Yearly engineer cost recovered" },
  "landing.proof.costNote": { pt: "Para time de 10 engenheiros de suporte", en: "Per 10-engineer support team" },
  "landing.features.overline": { pt: "// recursos", en: "// features" },
  "landing.features.title": { pt: "Feito para engenheiros de suporte.", en: "Built for support engineers." },
  "landing.features.titleHL": { pt: " Detestado pelo trabalho repetitivo.", en: " Hated by toil." },
  "landing.features.multi.overline": { pt: "multi-squad · multi-tenant", en: "multi-squad · multi-tenant" },
  "landing.features.multi.title": { pt: "Configure tipos de problema e fluxos de busca visualmente",
                                   en: "Configure problem types & search flows visually" },
  "landing.features.multi.desc": {
    pt: "Cada squad define seus tipos de problema, keywords e fluxos passo-a-passo. Drag-and-drop. Discovery automático de índices DynamoDB e log groups.",
    en: "Each squad defines its own problem types, keywords, and step-by-step flows. Drag-and-drop steps. Automatic discovery of DynamoDB indexes and log groups.",
  },
  "landing.features.creds.title": { pt: "Credenciais criptografadas", en: "Encrypted credentials" },
  "landing.features.creds.desc": {
    pt: "PATs e secrets armazenados com Fernet. Auth JWT. SSO no Enterprise. Isolamento Claude CLI por usuário.",
    en: "PATs and secrets stored with Fernet. JWT auth. SSO on Enterprise. Claude CLI isolation per user.",
  },
  "landing.features.progressive.title": { pt: "Busca progressiva", en: "Progressive search" },
  "landing.features.progressive.desc": {
    pt: "CloudWatch janela 30d primeiro. Se vazio, expande para 30–90d. Reduz custo de queries em produção.",
    en: "CloudWatch 30d window first. If empty, expands to 30-90d. Reduces production query cost.",
  },
  "landing.features.kb.overline": { pt: "base de conhecimento", en: "knowledge base" },
  "landing.features.kb.title": { pt: "Padrões que se atualizam sozinhos", en: "Patterns that update themselves" },
  "landing.features.kb.desc": {
    pt: "Cards fechados alimentam a KB. A IA detecta padrões recorrentes, taxas de resolução e tempos médios. Pareto pronto pra reunião de retrospectiva.",
    en: "Closed cards feed the KB. AI detects recurring patterns, resolution rates and average times. Pareto chart ready for retro meetings.",
  },
  "landing.pricing.overline": { pt: "// preços", en: "// pricing" },
  "landing.pricing.title": { pt: "Comece grátis. Pague quando crescer.", en: "Start free. Scale when it hurts." },
  "landing.pricing.subtitle": {
    pt: "Trial gratuito com 5 análises/mês. Sem cartão. Sem ligação de vendas no caminho.",
    en: "Free trial with 5 analyses/month. No credit card. No sales-call gauntlet.",
  },
  "landing.pricing.see": { pt: "Ver preços", en: "See pricing" },
  "landing.pricing.tryDemo": { pt: "Testar demo interativa", en: "Try interactive demo" },

  // Pipeline steps
  "flow.01.title": { pt: "Buscar", en: "Fetch" },
  "flow.01.desc": { pt: "Puxa o corpo do card + discussion do Azure DevOps / TFS / Jira / GitHub via PAT/token.",
                   en: "Pulls card body + discussion from Azure DevOps / TFS / Jira / GitHub via PAT/token." },
  "flow.02.title": { pt: "Extrair", en: "Extract" },
  "flow.02.desc": { pt: "Regex primeiro, depois Claude Sonnet preenche identificadores faltantes (tenant_id, order_id, request_id…).",
                   en: "Regex first, then Claude Sonnet fills missing identifiers (tenant_id, order_id, request_id…)." },
  "flow.03.title": { pt: "Classificar", en: "Classify" },
  "flow.03.desc": { pt: "Mapeia para os tipos de problema da sua squad via perfis de keywords configuráveis.",
                   en: "Maps to your squad's problem types via configurable keyword profiles." },
  "flow.04.title": { pt: "Consultar", en: "Query" },
  "flow.04.desc": { pt: "Discovery automático de índices DynamoDB, SQL paralelo, queries ES — tudo da sua config.",
                   en: "DynamoDB index discovery, parallel SQL, ES queries — all from your config." },
  "flow.05.title": { pt: "Logs", en: "Logs" },
  "flow.05.desc": { pt: "Busca progressiva CloudWatch / Datadog / Splunk / Loki: janela 30d, depois 30-90d.",
                   en: "Progressive CloudWatch / Datadog / Splunk / Loki search: 30d window first, then 30-90d." },
  "flow.06.title": { pt: "Analisar", en: "Analyze" },
  "flow.06.desc": { pt: "Claude Sonnet correlaciona dados + logs + discussions anteriores em uma árvore de hipóteses.",
                   en: "Claude Sonnet correlates data + logs + previous discussions into a hypothesis tree." },
  "flow.07.title": { pt: "Resolver", en: "Resolve" },
  "flow.07.desc": { pt: "Gera Hipóteses → Fatos → Causa raiz → Resolução. Pronto pra auditoria.",
                   en: "Generates Hypotheses → Facts → Root cause → Resolution. Audit-ready." },
  "flow.08.title": { pt: "Postar", en: "Post" },
  "flow.08.desc": { pt: "Resposta opcional de um clique de volta no card com confirmação.",
                   en: "Optional one-click reply back to the card with confirmation." },
  "flow.09.title": { pt: "Aprender", en: "Learn" },
  "flow.09.desc": { pt: "Base de conhecimento atualizada automaticamente com novos padrões e taxas de resolução.",
                   en: "Knowledge Base auto-updated with new patterns and resolution rates." },

  // ============ PRICING ============
  "pricing.h1": { pt: "Preços honestos para engenheiros honestos.", en: "Honest pricing for honest engineers." },
  "pricing.subtitle": {
    pt: "Cobramos por análise, não por usuário. Comece com 5 análises grátis/mês. Cancele quando quiser.",
    en: "We charge per analysis, not per user. Start with 5 free analyses/month. Cancel anytime.",
  },
  "pricing.popular": { pt: "★ MAIS POPULAR", en: "★ MOST POPULAR" },
  "pricing.startFree": { pt: "Começar grátis", en: "Start free" },
  "pricing.chooseStarter": { pt: "Escolher Starter", en: "Choose Starter" },
  "pricing.choosePro": { pt: "Escolher Pro", en: "Choose Pro" },
  "pricing.contactSales": { pt: "Falar com vendas", en: "Contact sales" },
  "pricing.band1.title": { pt: "Sem fidelidade", en: "No lock-in" },
  "pricing.band1.desc": { pt: "Cancele quando quiser. O acesso vale até o fim do período já pago, sem reembolso proporcional.",
                         en: "Cancel anytime. Access lasts until the end of the paid period, with no proportional refund.",
                         es: "Cancela cuando quieras. El acceso vale hasta el fin del período ya pagado, sin reembolso proporcional." },
  "pricing.band2.title": { pt: "PayPal · em breve", en: "PayPal · coming soon", es: "PayPal · próximamente" },
  "pricing.band2.desc": { pt: "Assinatura recorrente por cartão (Stripe), nos ciclos mensal, semestral ou anual. PayPal e USDC chegam em breve.",
                         en: "Recurring card subscription (Stripe) on monthly, semiannual or annual cycles. PayPal and USDC are coming soon.",
                         es: "Suscripción recurrente por tarjeta (Stripe) en ciclos mensual, semestral o anual. PayPal y USDC llegan pronto." },
  "pricing.band3.title": { pt: "Dados isolados", en: "Isolated data" },
  "pricing.band3.desc": { pt: "Cada org tem credenciais Fernet-encrypted no banco. Zero cross-tenant.",
                         en: "Each org has Fernet-encrypted credentials in DB. Zero cross-tenant." },
  "pricing.faq.title": { pt: "Perguntas frequentes", en: "Frequently asked" },
  "pricing.faq.q1": { pt: "O trial precisa de cartão?", en: "Does the trial require a credit card?" },
  "pricing.faq.a1": { pt: "Não. Cadastre, conecte suas integrações (ou rode no modo demo) e analise até 5 cards/mês. Sem cartão.",
                     en: "No. Sign up, connect your integrations (or run in demo mode) and analyze up to 5 cards/month. No card needed." },
  "pricing.faq.q2": { pt: "O que conta como 1 card?", en: "What counts as 1 card?" },
  "pricing.faq.a2": { pt: "Uma execução completa do pipeline: parse → query → análise IA → relatório. Reanálise do mesmo card não conta.",
                     en: "A full pipeline run: parse → query → AI analysis → report. Re-analysis of the same card doesn't count." },
  "pricing.faq.q3": { pt: "Vocês veem nossos dados?", en: "Do you see our data?" },
  "pricing.faq.a3": { pt: "Não. Credenciais encrypted com Fernet por org. Logs e dados ficam no seu pipeline.",
                     en: "No. Credentials Fernet-encrypted per org. Logs and data stay in your pipeline." },
  "pricing.faq.q4": { pt: "Como funciona Enterprise?", en: "How does Enterprise work?" },
  "pricing.faq.a4": { pt: "Conversamos sobre SSO, SLA, deploy on-prem se necessário, e preço por volume.",
                     en: "We chat about SSO, SLA, on-prem if needed, and volume pricing." },

  // Plan features
  "plan.trial.f1": { pt: "5 análises por mês", en: "5 analyses per month" },
  "plan.trial.f2": { pt: "1 squad", en: "1 squad" },
  "plan.trial.f3": { pt: "2 membros", en: "2 members" },
  "plan.trial.f4": { pt: "Integrações básicas", en: "Basic integrations" },
  "plan.trial.f5": { pt: "Comunidade", en: "Community" },
  "plan.starter.f1": { pt: "500 cards/mês", en: "500 cards/month" },
  "plan.starter.f2": { pt: "1 squad", en: "1 squad" },
  "plan.starter.f3": { pt: "5 membros", en: "5 members" },
  "plan.starter.f4": { pt: "TFS + CloudWatch + DynamoDB", en: "TFS + CloudWatch + DynamoDB" },
  "plan.starter.f5": { pt: "Suporte por email (48h)", en: "Email support (48h)" },
  "plan.starter.f6": { pt: "Knowledge Base automática", en: "Auto Knowledge Base" },
  "plan.pro.f1": { pt: "2.000 cards/mês", en: "2,000 cards/month" },
  "plan.pro.f2": { pt: "3 squads", en: "3 squads" },
  "plan.pro.f3": { pt: "15 membros", en: "15 members" },
  "plan.pro.f4": { pt: "Todas integrações (+ ES, SQL, APIs custom)", en: "All integrations (+ ES, SQL, custom APIs)" },
  "plan.pro.f5": { pt: "Chat 24h + métricas + ranking", en: "24h chat + metrics + ranking" },
  "plan.pro.f6": { pt: "API + Webhooks", en: "API + Webhooks" },
  "plan.pro.f7": { pt: "Onboarding assistido (2 sessões)", en: "Assisted onboarding (2 sessions)" },
  "plan.ent.f1": { pt: "Análises IA ilimitadas", en: "Unlimited AI analyses" },
  "plan.ent.f2": { pt: "Squads ilimitadas", en: "Unlimited squads" },
  "plan.ent.f3": { pt: "Até 50 membros", en: "Up to 50 members" },
  "plan.ent.f4": { pt: "SSO SAML/OIDC", en: "SSO SAML/OIDC" },
  "plan.ent.f5": { pt: "Cloud dedicado ou on-premise", en: "Dedicated cloud or on-prem" },
  "plan.ent.f6": { pt: "SLA 99.5% + Slack dedicado", en: "99.5% SLA + dedicated Slack" },
  "plan.ent.f7": { pt: "White-glove onboarding (5 sessões)", en: "White-glove onboarding (5 sessions)" },

  // ============ DEMO ============
  "demo.overline": { pt: "// sandbox · sem cadastro", en: "// sandbox · no signup required" },
  "demo.h1.1": { pt: "Veja o Ratchet diagnosticar um card", en: "Watch Ratchet diagnose a card" },
  "demo.h1.2": { pt: "em tempo real", en: "in real time" },
  "demo.subtitle": { pt: "Escolha um cenário típico e veja o pipeline rodando passo-a-passo com dados realistas. Mesmo fluxo do produto real.",
                    en: "Choose a typical scenario and see the pipeline running step-by-step with realistic data. Same flow as the real product." },
  "demo.run": { pt: "Rodar análise", en: "Run analysis" },
  "demo.tryAnother": { pt: "Testar outro cenário", en: "Try another scenario" },
  "demo.trace": { pt: "TRACE DE EXECUÇÃO", en: "EXECUTION TRACE" },
  "demo.report": { pt: "RELATÓRIO GERADO POR IA", en: "AI-GENERATED REPORT" },
  "demo.report.empty": { pt: "O relatório aparece aqui ao final da análise...", en: "The report appears here once analysis finishes..." },
  "demo.report.hypotheses": { pt: "Hipóteses", en: "Hypotheses" },
  "demo.report.facts": { pt: "Fatos coletados", en: "Collected facts" },
  "demo.report.rootCause": { pt: "Causa raiz", en: "Root cause" },
  "demo.report.resolution": { pt: "Resolução", en: "Resolution" },
  "demo.report.saved": { pt: "economizou", en: "saved" },
  "demo.progress": { pt: "progresso", en: "progress" },
  "demo.working": { pt: "Trabalhando", en: "Working" },
  "demo.cta.overline": { pt: "pronto pra conectar seus dados?", en: "ready to plug your own data?" },
  "demo.cta.title": { pt: "Inicie seu trial gratuito — 5 análises/mês, sem cartão.", en: "Start your free trial — 5 analyses/month, no credit card." },
  "demo.cta.create": { pt: "Criar conta", en: "Create account" },
  "demo.cta.pricing": { pt: "Ver preços", en: "See pricing" },
  "demo.scenario.scheduled_job.label": { pt: "Scheduled job não disparou", en: "Scheduled job didn't fire" },
  "demo.scenario.scheduled_job.desc": { pt: "Job agendado (cron / queue) não foi executado para um tenant",
                                       en: "Scheduled job (cron / queue) didn't run for a tenant" },
  "demo.scenario.duplicate_charge.label": { pt: "Cobrança duplicada", en: "Duplicate charge" },
  "demo.scenario.duplicate_charge.desc": { pt: "Cliente foi cobrado duas vezes na mesma transação",
                                          en: "Customer was charged twice for the same transaction" },
  "demo.scenario.api_timeout.label": { pt: "Timeout em endpoint", en: "Endpoint timeout" },
  "demo.scenario.api_timeout.desc": { pt: "API intermitentemente lenta em horário de pico",
                                     en: "API intermittently slow during peak hours" },

  // ============ AUTH ============
  "auth.welcomeBack": { pt: "Bem-vindo de volta", en: "Welcome back" },
  "auth.signinTo": { pt: "Entre no Ratchet", en: "Sign in to Ratchet" },
  "auth.signin.cta": { pt: "Entrar →", en: "Sign in →" },
  "auth.signin.testimonial": { pt: "\"Eu odiava segundas-feiras. Agora o Ratchet me entrega a resolução antes mesmo de eu abrir o ticket.\"",
                              en: "\"I used to dread Mondays. Now Ratchet hands me the resolution before I open the ticket.\"" },
  "auth.signin.testimonialFrom": { pt: "— de um engenheiro de plataforma real", en: "— from a real platform engineer" },
  "auth.noAccount": { pt: "Sem conta?", en: "No account?" },
  "auth.startFree": { pt: "Começar trial gratuito", en: "Start free trial" },
  "auth.alreadyCustomer": { pt: "Já é cliente?", en: "Already a customer?" },
  "auth.signup.title": { pt: "Crie seu workspace", en: "Create your workspace" },
  "auth.signup.startFree": { pt: "Começar grátis", en: "Start free" },
  "auth.signup.includes": { pt: "Trial gratuito inclui", en: "Free trial includes" },
  "auth.signup.b1": { pt: "5 análises por mês", en: "5 analyses per month" },
  "auth.signup.b2": { pt: "Todas integrações em modo sandbox", en: "All integrations in sandbox mode" },
  "auth.signup.b3": { pt: "Knowledge Base inicial", en: "Initial Knowledge Base" },
  "auth.signup.b4": { pt: "2 membros de equipe", en: "2 team members" },
  "auth.signup.noCard": { pt: "sem cartão de crédito", en: "no credit card required" },
  "auth.signup.yourName": { pt: "Seu nome", en: "Your name" },
  "auth.signup.org": { pt: "Nome da organização", en: "Organization name" },
  "auth.signup.workEmail": { pt: "Email corporativo", en: "Work email" },
  "auth.signup.pwd": { pt: "Senha (mín. 8)", en: "Password (min 8)" },
  "auth.signup.create": { pt: "Criar workspace →", en: "Create workspace →" },
  "auth.invalidCreds": { pt: "Credenciais inválidas", en: "Invalid credentials" },

  // ============ EMAIL OTP (verificação de signup) ============
  "auth.otp.title": { pt: "Verifique seu email", en: "Verify your email" },
  "auth.otp.subtitle": { pt: "Enviamos um código de 6 dígitos para {email}", en: "We sent a 6-digit code to {email}" },
  "auth.otp.verify": { pt: "Verificar →", en: "Verify →" },
  "auth.otp.resend": { pt: "Reenviar código", en: "Resend code" },
  "auth.otp.resendIn": { pt: "Reenviar em {n}s", en: "Resend in {n}s" },
  "auth.otp.useDifferent": { pt: "Usar outro email", en: "Use a different email" },
  "auth.otp.err.invalid": { pt: "Código inválido", en: "Invalid code" },
  "auth.otp.err.generic": { pt: "Algo deu errado — tente novamente", en: "Something went wrong — try again" },

  // ============ TOTP (2FA por authenticator) ============
  "auth.totp.step": { pt: "último passo", en: "last step" },
  "auth.totp.title": { pt: "Proteja sua conta", en: "Secure your account" },
  "auth.totp.subtitle": { pt: "Escaneie o QR com seu app autenticador (Google Authenticator, Authy, 1Password) e digite o código de 6 dígitos.", en: "Scan the QR with your authenticator app (Google Authenticator, Authy, 1Password) and enter the 6-digit code." },
  "auth.totp.manual": { pt: "Ou digite este código manualmente:", en: "Or enter this code manually:" },
  "auth.totp.enterCode": { pt: "Código do app", en: "Code from the app" },
  "auth.totp.confirm": { pt: "Ativar 2FA →", en: "Enable 2FA →" },
  "auth.totp.loginTitle": { pt: "Verificação em duas etapas", en: "Two-factor verification" },
  "auth.totp.loginSubtitle": { pt: "Digite o código de 6 dígitos do seu app autenticador.", en: "Enter the 6-digit code from your authenticator app." },
  "auth.totp.verify": { pt: "Verificar →", en: "Verify →" },
  "auth.totp.required": { pt: "Sua conta precisa configurar o autenticador antes de continuar.", en: "Your account must set up an authenticator before continuing." },
  "auth.totp.err.invalid": { pt: "Código inválido", en: "Invalid code" },

  // ============ ONBOARDING ============
  "onb.step": { pt: "etapa", en: "step" },
  "onb.of": { pt: "de", en: "of" },
  "onb.welcome.title": { pt: "Bem-vindo ao Ratchet", en: "Welcome to Ratchet" },
  "onb.welcome.desc": { pt: "3 passos rápidos até sua primeira análise.", en: "3 quick steps to your first analysis." },
  "onb.welcome.body": { pt: "O Ratchet vai conectar nas suas fontes de dados (Azure DevOps, AWS, GitHub, Jira, etc.) e rodar análises completas em segundos.",
                       en: "Ratchet will connect to your data sources (Azure DevOps, AWS, GitHub, Jira, etc.) and run complete analyses in seconds." },
  "onb.welcome.sandbox": { pt: "⚠️ Todas as integrações começam em",
                          en: "⚠️ All integrations start in" },
  "onb.welcome.sandboxMode": { pt: "sandbox mode", en: "sandbox mode" },
  "onb.welcome.sandbox2": { pt: " — você pode usar credenciais fake e ainda ver o pipeline funcionando.",
                           en: " — you can use fake credentials and still see the pipeline work." },
  "onb.welcome.lets": { pt: "Vamos lá", en: "Let's go" },
  "onb.azure.title": { pt: "Conectar Azure DevOps / TFS", en: "Connect Azure DevOps / TFS" },
  "onb.azure.desc": { pt: "Cole seu PAT e a URL da sua collection.", en: "Paste your PAT and your collection URL." },
  "onb.azure.optional": { pt: "(opcional para o trial — pode deixar vazio)", en: "(optional for trial — leave blank)" },
  "onb.aws.title": { pt: "Conectar AWS (opcional)", en: "Connect AWS (optional)" },
  "onb.aws.desc": { pt: "Para queries em DynamoDB e CloudWatch.", en: "For DynamoDB and CloudWatch queries." },
  "onb.aws.optional": { pt: "(opcional)", en: "(optional)" },
  "onb.ready.title": { pt: "Tudo pronto", en: "You're ready" },
  "onb.ready.desc": { pt: "Vamos para o dashboard analisar seu primeiro card.",
                     en: "Let's head to the dashboard and analyze your first card." },
  "onb.ready.allSet": { pt: "Pronto.", en: "All set." },
  "onb.ready.open": { pt: "Abrir dashboard", en: "Open dashboard" },

  // ============ APP / DASHBOARD ============
  "side.workspace": { pt: "workspace", en: "workspace" },
  "side.dashboard": { pt: "Dashboard", en: "Dashboard" },
  "side.cards": { pt: "Cards", en: "Cards" },
  "side.knowledge": { pt: "Knowledge Base", en: "Knowledge Base" },
  "side.integrations": { pt: "Integrações", en: "Integrations" },
  "side.team": { pt: "Equipe", en: "Team" },
  "side.apikeys": { pt: "API Keys", en: "API Keys" },
  "side.billing": { pt: "Cobrança", en: "Billing" },
  "side.signout": { pt: "Sair", en: "Sign out" },
  "dash.overline": { pt: "// dashboard", en: "// dashboard" },
  "dash.title": { pt: "Visão geral", en: "Overview" },
  "dash.analyze": { pt: "Analisar card", en: "Analyze card" },
  "dash.kpi.cards": { pt: "Cards neste período", en: "Cards this period" },
  "dash.kpi.cardsOf": { pt: "de", en: "of" },
  "dash.kpi.saved": { pt: "Minutos economizados", en: "Minutes saved" },
  "dash.kpi.savedSub": { pt: "nos cards recentes", en: "across recent cards" },
  "dash.kpi.integrations": { pt: "Integrações", en: "Integrations" },
  "dash.kpi.integrationsSub": { pt: "conectadas", en: "connected" },
  "dash.kpi.team": { pt: "Equipe", en: "Team" },
  "dash.kpi.teamMax": { pt: "máx", en: "max" },
  "dash.planUsage": { pt: "// uso do plano", en: "// plan usage" },
  "dash.upgrade": { pt: "Faça upgrade para continuar analisando", en: "Upgrade plan to keep analyzing" },
  "dash.recent": { pt: "// cards recentes", en: "// recent cards" },
  "dash.viewAll": { pt: "Ver todos", en: "View all" },
  "dash.noCards": { pt: "Nenhum card ainda", en: "No cards yet" },
  "dash.firstCard": { pt: "Analise seu primeiro card", en: "Analyze your first card" },

  // ============ CARDS ============
  "cards.overline": { pt: "// cards", en: "// cards" },
  "cards.title": { pt: "Cards de suporte", en: "Support cards" },
  "cards.new": { pt: "Analisar novo card", en: "Analyze new card" },
  "cards.newAnalysis": { pt: "// nova análise", en: "// new analysis" },
  "cards.extId": { pt: "ID externo", en: "External ID" },
  "cards.source": { pt: "Origem", en: "Source" },
  "cards.cardTitle": { pt: "Título do card", en: "Card title" },
  "cards.run": { pt: "Rodar análise →", en: "Run analysis →" },
  "cards.empty": { pt: "Nenhum card analisado ainda. Clique em \"Analisar novo card\" acima.",
                  en: "No cards analyzed yet. Click \"Analyze new card\" above." },
  "cards.col.status": { pt: "Status", en: "Status" },
  "cards.col.id": { pt: "ID", en: "ID" },
  "cards.col.title": { pt: "Título", en: "Title" },
  "cards.col.scenario": { pt: "Cenário", en: "Scenario" },
  "cards.col.saved": { pt: "Economia", en: "Saved" },
  "cards.col.date": { pt: "Data", en: "Date" },
  "card.back": { pt: "Voltar para cards", en: "Back to cards" },
  "card.pipeline": { pt: "// pipeline", en: "// pipeline" },
  "card.aiReport": { pt: "// relatório da IA", en: "// AI report" },
  "card.awaiting": { pt: "Aguardando análise final...", en: "Awaiting final analysis..." },

  // ============ INTEGRATIONS ============
  "integ.overline": { pt: "// integrações", en: "// integrations" },
  "integ.title": { pt: "Fontes conectadas", en: "Connected sources" },
  "integ.subtitle": { pt: "Conecte seus serviços. Credenciais são encrypted com Fernet.",
                     en: "Connect your services. Credentials are Fernet-encrypted." },
  "integ.add": { pt: "Adicionar integração", en: "Add integration" },
  "integ.type": { pt: "Tipo", en: "Type" },
  "integ.test": { pt: "Testar", en: "Test" },
  "integ.empty": { pt: "Nenhuma integração ainda. Adicione Azure DevOps, GitHub, Jira, AWS, Datadog ou um banco.",
                  en: "No integrations yet. Add Azure DevOps, GitHub, Jira, AWS, Datadog or a database." },
  "integ.category.ai": { pt: "Provedores de IA (BYO key)", en: "AI providers (BYO key)" },
  "integ.category.boards": { pt: "Issue trackers / Boards", en: "Issue trackers / Boards" },
  "integ.category.logs": { pt: "Logs / Observability", en: "Logs / Observability" },
  "integ.category.repos": { pt: "Repositórios de código", en: "Code repositories" },
  "integ.category.databases": { pt: "Bancos de dados", en: "Databases" },
  "integ.category.chat": { pt: "Chat / Notificações", en: "Chat / Notifications" },
  "integ.confirmDelete": { pt: "Remover integração?", en: "Delete integration?" },

  // ============ TEAM ============
  "team.overline": { pt: "// equipe", en: "// team" },
  "team.title": { pt: "Membros e convites", en: "Members & invites" },
  "team.invite": { pt: "Convidar por email", en: "Invite via email" },
  "team.role": { pt: "Papel", en: "Role" },
  "team.send": { pt: "Enviar convite", en: "Send invite" },
  "team.active": { pt: "// membros ativos", en: "// active members" },
  "team.pending": { pt: "// convites pendentes", en: "// pending invites" },
  "team.copyLink": { pt: "copiar link", en: "copy link" },

  // ============ BILLING ============
  "billing.overline": { pt: "// cobrança", en: "// billing" },
  "billing.title": { pt: "Plano e cobrança", en: "Plan & billing" },
  "billing.currentlyOn": { pt: "Você está atualmente no", en: "You're currently on" },
  "billing.active": { pt: "ativo", en: "active" },
  "billing.payCard": { pt: "Pagar com cartão", en: "Pay with card" },
  "billing.payPaypal": { pt: "Pagar via PayPal", en: "Pay via PayPal" },
  "billing.startingTier": { pt: "— plano inicial —", en: "— starting tier —" },
  "billing.notes": { pt: "// notas de pagamento", en: "// payment notes" },
  "billing.note1": { pt: "Stripe — pagamento por cartão, ativação imediata.",
                    en: "Stripe — card payment, instant activation." },
  "billing.note2": { pt: "PayPal — geramos cobrança manual; fatura no email em até 1 dia útil.",
                    en: "PayPal — manual invoice generated; emailed within 1 business day." },
  "billing.note3": { pt: "Enterprise — orçamento sob medida com SSO, SLA e on-prem opcional.",
                    en: "Enterprise — custom quote with SSO, SLA and optional on-prem." },
  "billing.confirming": { pt: "Confirmando seu pagamento...", en: "Confirming your payment..." },
  "billing.confirmingDesc": { pt: "Verificando com o Stripe. Geralmente leva alguns segundos.",
                             en: "Checking with Stripe. This usually takes a few seconds." },
  "billing.confirmed": { pt: "Pagamento confirmado", en: "Payment confirmed" },
  "billing.confirmedDesc": { pt: "Seu plano agora é", en: "Your plan is now" },
  "billing.goDash": { pt: "Ir para dashboard →", en: "Go to dashboard →" },

  // ============ API KEYS ============
  "apikeys.overline": { pt: "// api keys", en: "// api keys" },
  "apikeys.title": { pt: "Chaves de API", en: "API keys" },
  "apikeys.titleSub": { pt: "Acesso por API", en: "API access" },
  "apikeys.upgrade": { pt: "Faça upgrade para Pro para habilitar acesso programático.",
                     en: "Upgrade to Pro to enable programmatic access." },
  "apikeys.generate": { pt: "Gerar chave", en: "Generate key" },
  "apikeys.warning": { pt: "copie agora — não será exibida novamente", en: "copy now — won't be shown again" },
  "apikeys.keyName": { pt: "Nome da chave", en: "Key name" },
  "apikeys.noKeys": { pt: "Nenhuma API key ainda.", en: "No API keys yet." },

  // ============ KB ============
  "kb.overline": { pt: "// base de conhecimento", en: "// knowledge base" },
  "kb.title": { pt: "Padrões aprendidos", en: "Patterns learned" },
  "kb.subtitle.1": { pt: "cards analisados", en: "cards analyzed" },
  "kb.subtitle.2": { pt: "padrões identificados", en: "patterns identified" },
  "kb.occurrences": { pt: "ocorrências:", en: "occurrences:" },
  "kb.resolved": { pt: "resolvido:", en: "resolved:" },

  // ============ ADMIN ============
  "admin.overline": { pt: "// super-admin · todos os tenants", en: "// super-admin · all tenants" },
  "admin.title": { pt: "Visão geral da plataforma", en: "Platform overview" },
  "admin.kpi.orgs": { pt: "Organizações", en: "Organizations" },
  "admin.kpi.users": { pt: "Usuários", en: "Users" },
  "admin.kpi.cards": { pt: "Cards processados", en: "Cards processed" },
  "admin.dist": { pt: "// distribuição de planos", en: "// plan distribution" },
  "admin.orgs": { pt: "// todas organizações", en: "// all organizations" },

  // ============ PANEL STEP-UP (owners@) ============
  "panel.gate.overline": { pt: "acesso restrito", en: "restricted access" },
  "panel.gate.title": { pt: "Verificação do painel", en: "Panel verification" },
  "panel.gate.subtitle": { pt: "Enviamos um código para {email}. Digite-o para abrir o painel de clientes e receita.", en: "We sent a code to {email}. Enter it to open the customers & revenue panel." },
  "panel.gate.verify": { pt: "Abrir painel →", en: "Open panel →" },
  "panel.gate.resend": { pt: "Reenviar código", en: "Resend code" },
  "panel.gate.err": { pt: "Código inválido", en: "Invalid code" },

  // ============ FOOTER ============
  "footer.tagline": { pt: "Diagnóstico automatizado de cards de suporte. De 30 minutos a 2 minutos por análise.",
                     en: "Automated diagnosis of support cards. From 30 minutes to 2 minutes per analysis." },
  "footer.product": { pt: "Produto", en: "Product" },
  "footer.resources": { pt: "Recursos", en: "Resources" },
  "footer.company": { pt: "Empresa", en: "Company" },
  "footer.about": { pt: "Sobre", en: "About" },
  "footer.contact": { pt: "Contato", en: "Contact" },
  "footer.status": { pt: "Status", en: "Status" },
  "footer.legal": { pt: "Legal", en: "Legal", es: "Legal" },
  "footer.terms": { pt: "Termos de Uso", en: "Terms of Use", es: "Términos de Uso" },
  "footer.rights": { pt: "© 2026 Ratchet Labs. Todos os direitos reservados.", en: "© 2026 Ratchet Labs. All rights reserved." },
  "footer.builtFor": { pt: "Feito para engenheiros que vivem debugando.", en: "Built for engineers who debug for a living." },
};

const LangCtx = createContext({ lang: "pt", setLang: () => {} });

const VALID_LANGS = ["pt", "en", "es"];

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem("ratchet_lang");
    return VALID_LANGS.includes(stored) ? stored : "pt";
  });
  useEffect(() => { localStorage.setItem("ratchet_lang", lang); }, [lang]);
  const setLang = (l) => setLangState(VALID_LANGS.includes(l) ? l : "pt");
  return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}

export function useLang() { return useContext(LangCtx); }

export function useT() {
  const { lang } = useContext(LangCtx);
  return (key, fallback = "") => {
    if (lang === "es" && ES_OVERRIDES[key]) return ES_OVERRIDES[key];
    const entry = DICT[key];
    if (!entry) return fallback || key;
    // ES falls back to EN, then PT — so partial ES translations never show empty
    return entry[lang] ?? entry.en ?? entry.pt ?? fallback;
  };
}
